/* eslint-disable no-control-regex */
import * as yup from 'yup';

export const errorMessages = {
  firstName: 'Please enter your first name',
  lastName: 'Please enter your last name',
  email: 'Please enter a valid email address',
  confirmEmail: 'Emails do not match',
  confirmEmailRequired: 'Please confirm your email address',
  // Intentional! See DF-431 for more details
  establishmentType: 'Please select an organisation type',
  establishmentName: 'Please enter your organisation name',
  organisationName: 'Please enter your organisation name',
  address1: 'Please enter the first line of your address',
  town: 'Please enter your town',
  country: 'Please select your country',
  postcode: {
    uk: 'Please enter a valid UK postcode',
    non_uk: 'Please enter a valid postcode'
  },
};

const inputRegex = {
  // Recreates validate.js as used our back-end,
  // see https://github.com/ansman/validate.js/blob/master/validate.js#L1064
  EMAIL: {
    PATTERN: /^(?:[a-z0-9!#$%&'*+/=?^_`’'{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`’'{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])$/i,
    ERROR: errorMessages.email
  },
  NAME: {
    PATTERN: /^[a-zA-ZÀ-öø-ÿ,.\- `’'][a-zA-ZÀ-öø-ÿ,.\- `’']*$/,
    ERROR: "This field only accepts alphabetical characters, spaces, and , . - '"
  },
  ADDRESS: {
    PATTERN: /^[a-zA-ZÀ-öø-ÿ0-9 ]+[ _.,`’'/&\w-]*$/,
    ERROR: "This field only accepts alphabetical characters, spaces, and , . - '"
  },
  JOB: {
    PATTERN: /^[^-\s][ \w \s `’' . \-&_?!@()]*$/,
    ERROR: "This field only accepts alpha-numeric characters, and ' . - & _ ? ! ( ) @"
  },
  EDCO: {
    PATTERN: /^[^-\s][ \w \s `’' . \-&_?!@()]*$/,
    ERROR: "This field only accepts up to 100 alpha-numeric characters, and ' . - & _ ? ! ( )"
  },
  UK_POSTCODE: {
    PATTERN: /^((GIR 0AA)|((([A-Za-z][0-9][0-9]?)|(([A-Za-z][A-Ha-hJ-Yj-y][0-9][0-9]?)|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9]?[A-Za-z])))) [0-9][A-Za-z]{2}))$/,
    ERROR: errorMessages.postcode.uk
  },
  NON_UK_POSTCODE: {
    PATTERN: /[^(?!\s*$).+]/,
    ERROR: errorMessages.postcode.non_uk
  },
  EXTRA_SPACES: {
    PATTERN: /^\S.*\S$|^\S$/,
    ERROR: 'Please remove any leading or trailing spaces'
  }
};

// Resuable Yup fieldtype functions with build-in tests and default max length, as defined PSPs.
// Note, there IS a bit of duplication here since there's no easy way to make these
// rules completely modular but also keep them "functionised":
const requiredField = (emptyError: string, maxLength = 50) => yup.string()
  .required(emptyError)
  .max(maxLength, `Please enter a maximum of ${maxLength} characters`)
  // Only check against our 'extra spaces' regex when a value is entered; the above required() will catch empty values:
  .test('extra-spaces', inputRegex.EXTRA_SPACES.ERROR, value => (value ? Boolean(value.match(inputRegex.EXTRA_SPACES.PATTERN)) : true));

const optionalField = (maxLength = 50) => yup.string()
  .max(maxLength, `Please enter a maximum of ${maxLength} characters`)
  .test('extra-spaces', inputRegex.EXTRA_SPACES.ERROR, value => (value ? Boolean(value.match(inputRegex.EXTRA_SPACES.PATTERN)) : true));

const requiredFieldWithRegex = (regex: RegExp, regexError: string, emptyError: string, maxLength = 50) => yup.string()
  .required(emptyError)
  .max(maxLength, `Please enter a maximum of ${maxLength} characters`)
  .test('extra-spaces', inputRegex.EXTRA_SPACES.ERROR, value => (value ? Boolean(value.match(inputRegex.EXTRA_SPACES.PATTERN)) : true))
  .test('regex-check', regexError, value => (value ? Boolean(value.match(regex)) : false));

const optionalFieldWithRegex = (regex: RegExp, regexError: string, maxLength = 50) => yup.string()
  .max(maxLength, `Please enter a maximum of ${maxLength} characters`)
  .test('extra-spaces', inputRegex.EXTRA_SPACES.ERROR, value => (value ? Boolean(value.match(inputRegex.EXTRA_SPACES.PATTERN)) : true))
  .test('regex-check', regexError, value => (value ? Boolean(value.match(regex)) : true));

const requiredEmailWithRegex = (regex: RegExp, regexError: string, emptyError: string) => yup.string()
  .required(emptyError)
  // As per restrictions in the back-end:
  .max(255, 'Please enter a maximum of 255 characters')
  .test('extra-spaces', inputRegex.EXTRA_SPACES.ERROR, value => (value ? Boolean(value.match(inputRegex.EXTRA_SPACES.PATTERN)) : true))
  .test('regex-check', regexError, value => (value ? Boolean(value.match(regex)) : false));

export {
  inputRegex,
  requiredFieldWithRegex,
  optionalFieldWithRegex,
  requiredField,
  optionalField,
  requiredEmailWithRegex
};
